import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import { publicLedgerServiceContext } from '../../../../ns-services/contexts.js';

import { PublicLedgerService } from '../../../../ns-services/ns-public-ledger-service.js';

import { nsLayoutStyles, nsPersonRowStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';

import type Person from '../../../../models/person.js';
import type Company from '../../../../models/company.js';
import { personDisplayName, personInitials } from '../../../../helpers/person.js';
import type { Option } from '../../../../ns-elements/ns-multi-select.js';
import {
  MAX_GROUP_NAME_LENGTH,
  MAX_RULE_DEPTH,
  addGroup,
  addPerson,
  isSameRule,
  nodeAt,
  removeNode,
  renameGroup,
  ruleFromRequirement,
  setThreshold,
  summariseRule,
  shownGroupName,
  toRequestRule,
  validateRule,
  type RuleGroup,
  type RuleNode,
  type RulePath,
} from './signatory-change.js';
import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-multi-select.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import definition from '@job-schemas/public-ledger/update-config-key.json';
import { jobSchema } from '../../job-schema.js';

// The card's JOB_STATUS once the change has been requested: the card then only says what happens next
const REQUESTED = 'REQUESTED';

const pathKey = (path: RulePath) => path.join('.') || 'top';

// What the card says, so the change of who signs the company's topic (update-topic-key.ts) can say it of the topic
export interface RuleChangeCopy {
  intro: string;
  requested: string;
  // When there is nothing to change the rule of yet
  noTarget: string;
}

/**
 * The requester's card of a signatory change (D8, D18): an editor for the company's signature rule, nested to any depth,
 * with named groups and a k-of-n threshold in every group, and people chosen from every employee (a person may be in more
 * than one group). "Request change" sends the rule to public-ledger-update-threshold-key, which checks it again; the rule
 * takes effect only once the old and the new rule have both signed and the public ledger has applied it. "Cancel" ends
 * the change.
 */
@customElement(NSUpdatePublicLedgerConfigKey.is)
export default class NSUpdatePublicLedgerConfigKey extends NSJobComponent {
  static get is() {
    return 'update-public-ledger-config-key';
  }

  @consume({ context: publicLedgerServiceContext })
  protected _pLedgerService?: PublicLedgerService;

  @state()
  private __currentRule: RuleGroup | null = null;

  @state()
  private __rule: RuleGroup | null = null;

  @state()
  private __employees: Person[] = [];

  // Per group (by path): what the person typed in its picker, and whom they have picked but not added yet
  @state()
  private __queries: Record<string, string> = {};

  @state()
  private __picked: Record<string, Option[]> = {};

  @state()
  private __requested = false;

  @state()
  private __busy = false;

  // What the new rule is for: the company's public ledger account id here, the topic's plTopic id on the topic card
  private __targetId: string | null = null;

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { update: { type: 'hidden', label: 'Request change' }, cancel: { type: 'hidden', label: 'Cancel' } },
    });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    nsPersonRowStyles,
    css`
      .rule-group {
        border: 1px solid var(--wa-color-surface-border);
        border-radius: var(--wa-border-radius-m);
        padding: var(--wa-space-m);
        margin: 0 0 var(--wa-space-m);
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-s);
      }

      .rule-group .rule-group {
        margin-bottom: 0;
      }

      .rule-group__head,
      .rule-group__add {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--wa-space-s);
      }

      .rule-group__name {
        flex: 1 1 12rem;
      }

      .rule-group__threshold {
        display: flex;
        align-items: center;
        gap: var(--wa-space-xs);
      }

      .rule-group__threshold input {
        width: 5em;
      }

      .rule-group__add ns-multi-select {
        flex: 1 1 14rem;
      }

      .rule-summary {
        font-weight: 600;
      }

      .rule-problem {
        color: var(--wa-color-danger-on-quiet);
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();
    if (!this._db) throw new Error('Unable to find db service');
    if (!this._directory) throw new Error('Unable to find directory service');
    if (!this._job) throw new Error('Unable to find job');

    this.__requested = this._job.metadata.some((m) => m.key === 'JOB_STATUS' && m.value === REQUESTED);

    await this._directory.initialised;
    const companyId = this._directory.company?.id;
    if (!companyId) throw new Error("Missing the person's company");
    const company = (await this._db.getById('company', companyId)) as Company | undefined;
    if (!company) throw new Error('Unable to find the company');

    const { current, targetId } = await this._loadRule(company);
    this.__targetId = targetId;
    this.__currentRule = current;
    this.__rule = this.__currentRule ?? { keyType: 'THRESHOLD_KEY', name: null, threshold: 1, keys: [] };

    const people = await this._db.query('people', { companyId: { $eq: companyId } });
    this.__employees = (people.results as unknown as Person[]).filter((p) => p.activeProfile !== false);
  }

  /** The rule now and what a new one is for: the company account's rule and its public ledger account. */
  protected async _loadRule(company: Company): Promise<{ current: RuleGroup | null; targetId: string | null }> {
    return {
      current: ruleFromRequirement(company.ledgerCred?.signatureRequirement),
      targetId: company.ledgerCred?.accountId ?? null,
    };
  }

  /** Send the new rule for checking and recording: public-ledger-update-threshold-key. */
  protected async _propose(workflowId: string, targetId: string, rule: RuleGroup): Promise<void> {
    if (!this._pLedgerService) throw new Error('Missing required public ledger service');
    await this._pLedgerService.proposeSignatureRule(workflowId, targetId, rule);
  }

  protected get _copy(): RuleChangeCopy {
    return {
      intro:
        'Choose who signs for the company on the public ledger. Each group needs a number of its signatories, or of ' +
        'the groups inside it, to sign. The current signatories and the new ones all sign this change.',
      requested:
        'The change has been requested. Anyone new without a public ledger account has been asked to create one, ' +
        'and then everyone in the current and the new rule will be asked to sign. The new rule takes effect once the ' +
        'public ledger has applied it, and you will be told how it ends.',
      noTarget: "The company's public ledger account isn't set up yet",
    };
  }

  private __nameOf = (personId: string) => {
    const person = this.__employees.find((p) => p.id === personId);
    return person ? personDisplayName(person) : 'A former employee';
  };

  private __change(rule: RuleGroup) {
    this.clearError();
    this.__rule = rule;
  }

  private __options(group: RuleGroup, path: RulePath): Option[] {
    const query = (this.__queries[pathKey(path)] ?? '').trim().toLowerCase();
    const inGroup = new Set(group.keys.flatMap((k) => (k.keyType === 'PERSON_ID' ? [k.signatoryId] : [])));
    return this.__employees
      .filter((p) => !inGroup.has(p.id))
      .map((p) => ({ value: p.id, label: personDisplayName(p) }))
      .filter((o) => !query || o.label.toLowerCase().includes(query));
  }

  private __addPicked(path: RulePath) {
    if (!this.__rule) return;
    const key = pathKey(path);
    let rule = this.__rule;
    for (const option of this.__picked[key] ?? []) rule = addPerson(rule, path, option.value);
    this.__picked = { ...this.__picked, [key]: [] };
    this.__queries = { ...this.__queries, [key]: '' };
    this.__change(rule);
  }

  // Why the rule can't be requested yet, or null when it can
  private get __problem(): string | null {
    if (!this.__rule) return 'Loading the current signatories';
    const invalid = validateRule(this.__rule);
    if (invalid) return invalid;
    if (isSameRule(this.__rule, this.__currentRule)) return 'Change the rule to request a change';
    return null;
  }

  private async __requestChange() {
    if (!this._job?.workflow?.id) throw new Error('Unable to find the change workflow');
    if (!this.__rule || this.__problem) return;
    if (!this.__targetId) {
      this.showError(this._copy.noTarget);
      return;
    }

    this.__busy = true;
    this.loading('Requesting the change');
    try {
      await this._propose(this._job.workflow.id, this.__targetId, toRequestRule(this.__rule));
      await this.updateMetadata('JOB_STATUS', REQUESTED);
      this.__requested = true;
      this.loaded();
      await this.outcome('update');
    } catch (err) {
      this.loaded();
      this.showError(err, 'Unable to request the change');
    } finally {
      this.__busy = false;
    }
  }

  private async __cancel() {
    this.__busy = true;
    try {
      await this.outcome('cancel');
    } catch (err) {
      this.showError(err, 'Unable to cancel the change');
    } finally {
      this.__busy = false;
    }
  }

  private __renderPerson(personId: string, path: RulePath) {
    const person = this.__employees.find((p) => p.id === personId);
    const name = this.__nameOf(personId);
    return html`
      <div class="person-row">
        <div class="person-row__primary">
          <div class="person-row__avatar horizontal center-center" aria-hidden="true">
            ${person ? personInitials(person) : '?'}
          </div>
          <div class="person-row__cell">
            <span class="value">${name}</span>
            <span class="caption">${person?.jobTitle ?? ''}</span>
          </div>
        </div>
        <button
          type="button"
          class="person-row__action"
          aria-label="Remove ${name} from this group"
          ?disabled=${this.__busy}
          @click=${() => this.__rule && this.__change(removeNode(this.__rule, path))}
        >
          <ns-icon icon="times"></ns-icon>
        </button>
      </div>
    `;
  }

  private __renderNode(node: RuleNode, path: RulePath, depth: number): unknown {
    if (node.keyType === 'PERSON_ID') return this.__renderPerson(node.signatoryId, path);
    return this.__renderGroup(node, path, depth);
  }

  private __renderGroup(group: RuleGroup, path: RulePath, depth: number) {
    const key = pathKey(path);
    const n = group.keys.length;
    const label = shownGroupName(group.name) || (path.length === 0 ? 'Everyone who signs' : 'Group');
    const picked = this.__picked[key] ?? [];
    return html`
      <fieldset class="rule-group">
        <legend class="visually-hidden">${label}</legend>
        <div class="rule-group__head">
          <wa-input
            class="rule-group__name"
            label="Group name"
            placeholder=${path.length === 0 ? 'Name this rule (optional)' : 'Name this group, for example Directors'}
            maxlength=${MAX_GROUP_NAME_LENGTH}
            .value=${shownGroupName(group.name)}
            ?disabled=${this.__busy}
            @change=${(e: Event) =>
              this.__rule && this.__change(renameGroup(this.__rule, path, (e.target as HTMLInputElement).value))}
          ></wa-input>
          <label class="rule-group__threshold">
            Needs
            <input
              type="number"
              min="1"
              max=${Math.max(1, n)}
              step="1"
              aria-label="Signatures ${label} needs"
              .value=${String(group.threshold)}
              ?disabled=${this.__busy || n === 0}
              @change=${(e: Event) => {
                if (!this.__rule) return;
                const input = e.target as HTMLInputElement;
                const rule = setThreshold(this.__rule, path, Number(input.value));
                this.__change(rule);
                // The input shows the clamped value even when the rule didn't change
                const changed = nodeAt(rule, path);
                input.value = String(changed?.keyType === 'THRESHOLD_KEY' ? changed.threshold : 1);
              }}
            />
            of ${n}
          </label>
          ${path.length > 0
            ? html`
                <wa-button
                  appearance="outlined"
                  variant="brand"
                  size="small"
                  ?disabled=${this.__busy}
                  @click=${() => this.__rule && this.__change(removeNode(this.__rule, path))}
                >
                  Remove group
                </wa-button>
              `
            : nothing}
        </div>
        ${repeat(
          group.keys,
          (child, idx) => (child.keyType === 'PERSON_ID' ? `p-${child.signatoryId}` : `g-${key}-${idx}`),
          (child, idx) => this.__renderNode(child, [...path, idx], depth + 1),
        )}
        <div class="rule-group__add">
          <ns-multi-select
            no-auto-fill
            page-size="10"
            label="Add people to ${label}"
            placeholder="Search employees by name..."
            .options=${this.__options(group, path)}
            .selected=${picked}
            @search-input=${(e: CustomEvent<{ query: string }>) =>
              (this.__queries = { ...this.__queries, [key]: e.detail.query })}
            @selection-changed=${(e: CustomEvent<{ selected: Option[] }>) =>
              (this.__picked = { ...this.__picked, [key]: e.detail.selected })}
          ></ns-multi-select>
          <wa-button
            variant="brand"
            size="small"
            ?disabled=${this.__busy || picked.length === 0}
            @click=${() => this.__addPicked(path)}
          >
            Add
            <span class="visually-hidden">the chosen people to ${label}</span>
          </wa-button>
          <wa-button
            appearance="outlined"
            variant="brand"
            size="small"
            ?disabled=${this.__busy || depth >= MAX_RULE_DEPTH}
            @click=${() => this.__rule && this.__change(addGroup(this.__rule, path))}
          >
            Add group
            <span class="visually-hidden">inside ${label}</span>
          </wa-button>
        </div>
      </fieldset>
    `;
  }

  render() {
    if (this.__requested) {
      return html`
        <ns-job-layout>
          <div class="job-panel">
            <p>${this._copy.requested}</p>
          </div>
        </ns-job-layout>
      `;
    }
    const problem = this.__problem;
    return html`
      <ns-job-layout>
        <div class="job-panel">
          <p>${this._copy.intro}</p>
          ${this.__currentRule
            ? html`
                <p>Now: ${summariseRule(this.__currentRule, this.__nameOf)}</p>
              `
            : nothing}
        </div>
        <div class="job-panel">${this.__rule ? this.__renderGroup(this.__rule, [], 1) : nothing}</div>
        <div class="job-panel">
          <p class="rule-summary" aria-live="polite">
            New rule: ${this.__rule ? summariseRule(this.__rule, this.__nameOf) : ''}
          </p>
          ${problem
            ? html`
                <p class="rule-problem">${problem}</p>
              `
            : nothing}
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this.__requested) return nothing;
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this.__busy}
        @click=${() => this.__cancel()}
      >
        Cancel
      </wa-button>
      <wa-button
        variant="brand"
        type="button"
        ?disabled=${this.__busy || !!this.__problem}
        @click=${() => this.__requestChange()}
      >
        Request change
      </wa-button>
    `;
  }
}
